<template>
	<b-container fluid class="rb-privacy">
		<b-row>
			<b-col xl="8" offset-xl="2" lg="10" offset-lg="1" class="my-5">
				<input type="file" accept="video/*" ref="fileInput" @change="onFileInputChange">\

				<rb-video-player
					v-if="videoUrl"
					:sources="[{
						url: videoUrl,
						quality: {
							name: 'FHD',
							size: 1080
						}
					}]"
				/>

				<canvas ref="canvas" width="1200" height="400" />
			</b-col>
		</b-row>
	</b-container>
</template>

<script>
	import RbVideoPlayer from '../../components/RbVideoPlayer/RbVideoPlayer';

	const config = {
		fftResolution: 1024,
		smoothingTimeConstant: 0.6,
		processorBufferSize: 512
	};

	function getBuffer(file) {
		let accept, promise = new Promise(a => {
			accept = a;
		});

		let reader = new FileReader();

		reader.onload = function () {
			accept(reader.result);
		};

		reader.readAsArrayBuffer(file);

		return promise;
	}

	async function proccessAudio(file) {
		const videoFileAsBuffer = await getBuffer(file);
		const audioContext = new AudioContext();
		const audioBuffer = await audioContext.decodeAudioData(videoFileAsBuffer);

		const offlineAudioContext = new OfflineAudioContext(
			audioBuffer.numberOfChannels,
			audioBuffer.length,
			audioBuffer.sampleRate);

		// Create the source
		const soundSource = offlineAudioContext.createBufferSource();
		soundSource.buffer = audioBuffer;
		soundSource.channelCount = audioBuffer.numberOfChannels;

		// Create an Analyser to capture frequency data
		const analyser = offlineAudioContext.createAnalyser();
		analyser.fftSize = config.fftResolution;
		analyser.smoothingTimeConstant = config.smoothingTimeConstant;

		// Create a buffer to store the frequency data, it's sized is:
		// The length of the audio buffer divided by the size of each chunk the processor will analyze times the resolution over 2
		const dataBuffer = new Uint8Array((audioBuffer.length / config.processorBufferSize) * (config.fftResolution / 2));

		// Create a processor node to process the audio buffer sequentially
		const processor = offlineAudioContext.createScriptProcessor(config.processorBufferSize, 1, 1);

		let offset = 0;
		processor.onaudioprocess = () => {
			const freqData = new Uint8Array(dataBuffer.buffer, offset, analyser.frequencyBinCount);
			analyser.getByteFrequencyData(freqData);

			offset += analyser.frequencyBinCount
		};



		soundSource.connect(analyser);
		soundSource.connect(processor);
		processor.connect(offlineAudioContext.destination);

		soundSource.start();

		await offlineAudioContext.startRendering();

		const dataArray = Array.from(dataBuffer);

		const outputObject = {
			data: null,
			stride: config.fftResolution/2,
			tickCount: Math.ceil(audioBuffer.length / config.processorBufferSize),
			maxFreq: offlineAudioContext.sampleRate / 2, // max freq is always half the sample rate
			duration: audioBuffer.duration
		};

		// Create a 2d array
		const output = Array.from(Array(outputObject.tickCount+1)).map(()=> Array.from(Array(outputObject.stride)));

		for(let row = 0; row < outputObject.stride; row += 1){
			for(let col = 0; col <= outputObject.tickCount; col += 1){
				output[col][row] = dataArray[col * outputObject.stride + row]
			}
		}

		outputObject.data = output;

		return outputObject;
	}


	/**
	 * @typedef {object} SpectralData
	 * @property {Number[][]}	data    	FFT Data by time and then frequency
	 * @property {number}       stride      Number of data points in a data block
	 * @property {number}       rowCount    Number of rows of data
	 * @property {number}       maxFreq     Maximum frequency of the data
	 * @property {number}       duration    Audio buffer duration in seconds
	 */


	export default {
		name: 'SubtitleSuite',
		components: { RbVideoPlayer },
		metaInfo() {
			return {
				title: 'Editor de Legenda'
			}
		},
		data: () => ({
			loaded: false,
			videoUrl: null,
			/** @type {?SpectralData} **/
			spectralData: null
		}),
		async mounted() {
		},
		methods: {
			async onFileInputChange(e) {
				let file = e.target.files[0];
				this.videoUrl = URL.createObjectURL(file);

				this.spectralData = await proccessAudio(file);

				window.requestAnimationFrame(() => this.drawSpectrogram());
			},
			drawSpectrogram() {
				let canvas = this.$refs.canvas;
				let ctx = canvas.getContext('2d');

				let { width, height } = canvas.getBoundingClientRect();

				let { data } = this.spectralData;

				ctx.fillStyle = 'rgba(0, 0, 0, 1)';
				ctx.fillRect(0, 0, width, height);

				const timeRatio = data.length / width;
				const frequencyRatio = data[0].length / width;
				const frequencyRatioInt = Math.ceil(frequencyRatio);


				for (let x = 0; x < width; x++) {
					for (let y = height; y > 0; y--) {
						let values = [];
						let startFreq = Math.floor(frequencyRatio * (height - y));

						for (let f = 0; f < frequencyRatioInt; f++) {
							let r = Math.max(1, frequencyRatio - f);
							values.push(data[x][startFreq + f] * r);
						}

						let avg = values.reduce((a, b) => a + b)/values.length;

						ctx.fillStyle = `rgba(${Math.floor(avg)}, 0, 0, 1)`;
						ctx.fillRect(x, y, 1, 1);
					}
				}
			}
		}
	}
</script>
<style lang="scss" scoped>
	.rb-privacy {
		p {
			line-height: 1.2em;
			clear: both;
			text-indent: 1.5em;
			margin-bottom: 1.5em;
		}

		h3, h4 {
			font-weight: bold;
		}

		h4 {
			margin-bottom: 1em;
		}

		.rb-privacy-spacer {
			margin-top: 25px;
		}
	}
</style>